/// <reference types="@webgpu/types" />
import {Three as THREE} from "#three-texture-streaming/three-exports"
import type {Size2Like, Vector2Like} from "@cm/math"
import type {AtlasFormat} from "#three-texture-streaming/texture-tile-constants"
import {TILE_LOCK_FRAMES} from "#three-texture-streaming/texture-tile-constants"
import type {TextureTileId} from "#three-texture-streaming/texture-tile-id"
import type {ThreeTextureTileAtlasCallback} from "#three-texture-streaming/three-texture-tile-atlas-callback"
import {assertNever, PriorityQueue} from "@cm/utils"
import {sceneLogger as logger} from "#three-texture-streaming/scene-logger"
import type {IWebgl2RgbaStaging} from "#three-texture-streaming/i-webgl2-rgba-staging"
import type {PixelTileResponse} from "#three-texture-streaming/tile-request-response"

export class ThreeTextureTileAtlas {
    constructor(
        readonly renderer: THREE.WebGPURenderer,
        private callback: ThreeTextureTileAtlasCallback,
        readonly tileSize: Size2Like,
        readonly tileMargin: number,
        readonly format: AtlasFormat,
        minNumTiles: number,
        private readonly createWebgl2RgbaStaging?: (width: number, height: number) => IWebgl2RgbaStaging,
    ) {
        this._tileSizeWithMargin = {
            width: this.tileSize.width + this.tileMargin * 2,
            height: this.tileSize.height + this.tileMargin * 2,
        }
        this._atlasTexture = this.createAtlasTexture(minNumTiles, false)
        this._textureFiltering = false
        this.enqueueSlots()
    }

    dispose(): void {
        this._atlasTexture.dispose()
        this._webgl2StagingMip0?.dispose()
        this._webgl2StagingMip0 = null
        this._webgl2StagingMip1?.dispose()
        this._webgl2StagingMip1 = null
    }

    get tileSizeWithMargin(): Size2Like {
        return this._tileSizeWithMargin
    }

    get atlasTexture(): THREE.DataArrayTexture {
        return this._atlasTexture
    }

    get capacity(): number {
        return this._numLayers * this._numTilesX * this._numTilesY
    }

    addTile(
        textureTileId: TextureTileId,
        tileImage: PixelTileResponse,
        coarserTileImage: PixelTileResponse | null,
        lockTile: boolean,
        frame: number,
    ): AtlasTileInfo {
        const atlasTileInfo = this.setAtlasTileInfo(textureTileId, lockTile)
        this.updateAtlasTileImage(atlasTileInfo, tileImage, "fine")
        if (coarserTileImage) {
            this.updateAtlasTileImage(atlasTileInfo, coarserTileImage, "coarse")
        }
        this.markTileAsUsed(textureTileId, frame)
        return atlasTileInfo
    }

    setFiltering(linear: boolean): void {
        if (this._textureFiltering === linear) {
            return
        }
        this._textureFiltering = linear
        // WebGPU samplers are immutable — needsUpdate alone triggers createTexture on an
        // already-initialized texture and throws "Texture already initialized". Disposing
        // first frees the GPU resources so Three.js can recreate texture+sampler with the
        // new filter settings on the next render.
        this._atlasTexture.minFilter = linear ? THREE.LinearMipmapLinearFilter : THREE.NearestMipmapLinearFilter
        this._atlasTexture.magFilter = linear ? THREE.LinearFilter : THREE.NearestFilter
        this.disposeAndEvictAll()
        this._atlasTexture.needsUpdate = true
    }

    willOverflowOnNextAdd(currentFrame: number): boolean {
        const lru = this._atlasTilePriorityQueue.peek()
        if (!lru || !lru.occupiedBy) return false // free slot available
        if (this._atlasTilePriorityQueue.peekPriority()! <= TILE_PRIORITY_LOCKED) return true // only permanently-locked tiles remain
        return currentFrame - lru.lastUsedFrame <= TILE_LOCK_FRAMES // LRU tile still within lock window
    }

    expand(minNumTiles: number): void {
        if (minNumTiles <= this.capacity) {
            return
        }
        logger.log(`ThreeTextureTileAtlas: resizing atlas (${this.format})`)
        this.disposeAndEvictAll()
        this._atlasTexture = this.createAtlasTexture(minNumTiles, this._textureFiltering ?? false)
        this.enqueueSlots()
    }

    removeTile(textureTileId: TextureTileId): void {
        const atlasTileInfo = this._atlasTileInfoByTextureTileId.get(textureTileId)
        if (!atlasTileInfo) {
            throw new Error("Tile is not present in atlas")
        }

        this._atlasTileInfoByTextureTileId.delete(textureTileId)
        this._atlasTilePriorityQueue.remove(atlasTileInfo)
        atlasTileInfo.occupiedBy = null
        this._atlasTilePriorityQueue.enqueue(atlasTileInfo, TILE_PRIORITY_FREE)
    }

    markTileAsUsed(textureTileId: TextureTileId, frame: number): void {
        const atlasTileInfo = this._atlasTileInfoByTextureTileId.get(textureTileId)
        if (!atlasTileInfo) {
            throw new Error("Tile is not present in atlas")
        }
        atlasTileInfo.lastUsedFrame = frame
        this._atlasTilePriorityQueue.moveToBack(atlasTileInfo)
    }

    hasTile(textureTileId: TextureTileId): boolean {
        return this._atlasTileInfoByTextureTileId.has(textureTileId)
    }

    private createAtlasTexture(minNumTiles: number, linear: boolean): THREE.DataArrayTexture {
        const getMaxTextureSize = (): number => {
            const anyBackend = this.renderer.backend as any
            return (
                anyBackend.device?.limits.maxTextureDimension2D ??
                anyBackend.gl?.getParameter(anyBackend.gl.MAX_TEXTURE_SIZE) ?? // WebGL fallback backend
                4096 // safe default
            )
        }
        const getMaxTextureLayers = (): number => {
            const anyBackend = this.renderer.backend as any
            return (
                anyBackend.device?.limits.maxTextureArrayLayers ??
                anyBackend.gl?.getParameter(anyBackend.gl.MAX_ARRAY_TEXTURE_LAYERS) ?? // WebGL fallback backend
                256 // safe default
            )
        }
        const determineSizes = (
            minNumTiles: number,
            maxTextureSize: number,
            maxTextureLayers: number,
        ): {numTilesX: number; numTilesY: number; numLayers: number} => {
            const mAb = Math.floor(maxTextureSize / Math.max(this._tileSizeWithMargin.width, this._tileSizeWithMargin.height))
            const mC = maxTextureLayers
            if (mAb * mAb * mC < minNumTiles) throw new Error(`Cannot fit ${minNumTiles} tiles in atlas (max ${mAb}x${mAb}x${mC})`)
            const cLo = Math.ceil(minNumTiles / (mAb * mAb))
            const tryC = (c: number): {numTilesX: number; numTilesY: number; numLayers: number} | null => {
                if (c < cLo || c > mC) return null
                const K = Math.ceil(minNumTiles / c)
                const a = Math.max(Math.ceil(K / mAb), Math.min(mAb, Math.ceil(Math.sqrt(K))))
                const b = Math.ceil(K / a)
                return {numTilesX: a, numTilesY: b, numLayers: c}
            }
            const cIdeal = Math.min(mC, Math.max(cLo, Math.ceil(Math.cbrt(minNumTiles))))
            let best: {numTilesX: number; numTilesY: number; numLayers: number} | null = null
            for (const c of [cLo, cIdeal, mC]) {
                const candidate = tryC(c)
                if (candidate && (!best || candidate.numTilesX * candidate.numTilesY * candidate.numLayers < best.numTilesX * best.numTilesY * best.numLayers))
                    best = candidate
            }
            if (!best) {
                throw new Error(`Failed to determine atlas size for ${minNumTiles} tiles; this should never happen`)
            }
            return best
        }
        const maxTextureSize = getMaxTextureSize()
        const maxTextureLayers = getMaxTextureLayers()
        const {numTilesX, numTilesY, numLayers} = determineSizes(minNumTiles, maxTextureSize, maxTextureLayers)
        this._numLayers = numLayers
        this._numTilesX = numTilesX
        this._numTilesY = numTilesY
        const atlasWidth = this._numTilesX * this._tileSizeWithMargin.width
        const atlasHeight = this._numTilesY * this._tileSizeWithMargin.height
        const bytesPerTexel = this.format === "r8" ? 1 : 4
        const allocationMB = ((atlasWidth * atlasHeight * numLayers * bytesPerTexel * 1.25) / (1024 * 1024)).toFixed(1)
        logger.log(
            `ThreeTextureTileAtlas: creating atlas texture (${this._numTilesX * this.tileSizeWithMargin.width}x${this._numTilesY * this.tileSizeWithMargin.height}x${this._numLayers}) format=${this.format} for ${this._numLayers * this._numTilesX * this._numTilesY} tiles of size ${this.tileSizeWithMargin.width}x${this.tileSizeWithMargin.height} (margin: ${this.tileMargin}). Allocating ${allocationMB} MB`,
        )
        const threeFormat = this.format === "r8" ? THREE.RedFormat : THREE.RGBAFormat
        const mip1W = Math.floor(atlasWidth / 2)
        const mip1H = Math.floor(atlasHeight / 2)
        const tex = new THREE.DataArrayTexture(null, atlasWidth, atlasHeight, numLayers)
        tex.mipmaps = [
            {data: null as unknown as Uint8Array, width: atlasWidth, height: atlasHeight},
            {data: null as unknown as Uint8Array, width: mip1W, height: mip1H},
        ]
        tex.generateMipmaps = false
        tex.type = THREE.UnsignedByteType
        tex.format = threeFormat
        tex.colorSpace = THREE.LinearSRGBColorSpace
        // source.dataReady = false: Three.js allocates the GPU texture (createTexture) but skips
        // the CPU-to-GPU upload (updateTexture) so we can avoid allocating the backing arraybuffer
        tex.source.dataReady = false
        tex.needsUpdate = true
        tex.minFilter = linear ? THREE.LinearMipmapLinearFilter : THREE.NearestMipmapLinearFilter
        tex.magFilter = linear ? THREE.LinearFilter : THREE.NearestFilter
        return tex
    }

    private enqueueSlots(): void {
        this._atlasTilePriorityQueue.clear()
        for (let layer = 0; layer < this._numLayers; layer++) {
            for (let y = 0; y < this._numTilesY; y++) {
                for (let x = 0; x < this._numTilesX; x++) {
                    this._atlasTilePriorityQueue.enqueue(
                        {
                            layer,
                            tileIndex: {x, y},
                            occupiedBy: null,
                            lastUsedFrame: 0,
                        },
                        TILE_PRIORITY_FREE,
                    )
                }
            }
        }
    }

    private disposeAndEvictAll(): void {
        this._atlasTexture.dispose()
        this._gpuDevice = null
        this._gpuTexture = null
        const ids = Array.from(this._atlasTileInfoByTextureTileId.keys())
        for (const id of ids) {
            this.removeTile(id)
            this.notifyTileEvicted(id)
        }
    }

    private setAtlasTileInfo(tileToSet: TextureTileId | null, lockTile: boolean): InternalAtlasTileInfo {
        if (tileToSet && this.hasTile(tileToSet)) {
            throw new Error("Tile is already present in atlas")
        }
        const bestTileToReplace = this._atlasTilePriorityQueue.dequeue()
        if (!bestTileToReplace) {
            throw new Error("No available tiles in atlas to replace; this should never happen")
        }
        if (bestTileToReplace.occupiedBy) {
            if (bestTileToReplace.occupiedBy === tileToSet) {
                throw new Error("Inconsistent atlas tile state; this should never happen")
            }
            if (!this._atlasTileInfoByTextureTileId.has(bestTileToReplace.occupiedBy)) {
                throw new Error("Inconsistent atlas tile state; this should never happen")
            }
            this._atlasTileInfoByTextureTileId.delete(bestTileToReplace.occupiedBy)
            this.notifyTileEvicted(bestTileToReplace.occupiedBy)
        }
        bestTileToReplace.occupiedBy = tileToSet
        if (bestTileToReplace.occupiedBy) {
            this._atlasTileInfoByTextureTileId.set(bestTileToReplace.occupiedBy, bestTileToReplace)
        }
        const priority = bestTileToReplace.occupiedBy ? (lockTile ? TILE_PRIORITY_LOCKED : TILE_PRIORITY_OCCUPIED) : TILE_PRIORITY_FREE
        this._atlasTilePriorityQueue.enqueue(bestTileToReplace, priority)
        return bestTileToReplace
    }

    private notifyTileEvicted(evictedTile: TextureTileId): void {
        this.callback.onTileEvictedCallback?.(evictedTile)
    }

    private updateAtlasTileImage(atlasTileInfo: InternalAtlasTileInfo, src: PixelTileResponse, mipLevelSlot: "fine" | "coarse"): void {
        const dstMipLevel = mipLevelSlot === "fine" ? 0 : 1
        const mipScale = 1 << dstMipLevel
        const scaledTileSize = {
            width: this.tileSizeWithMargin.width / mipScale,
            height: this.tileSizeWithMargin.height / mipScale,
        }
        if (src.width > scaledTileSize.width || src.height > scaledTileSize.height) {
            throw new Error("Tile image size is larger than the atlas tile size")
        }
        const dstX = atlasTileInfo.tileIndex.x * scaledTileSize.width
        const dstY = atlasTileInfo.tileIndex.y * scaledTileSize.height
        const dstZ = atlasTileInfo.layer
        const gpuCtx = this.getGpuUploadContext()
        const bytesPerTexel = this.format === "r8" ? 1 : 4

        if (gpuCtx) {
            gpuCtx.device.queue.writeTexture(
                {texture: gpuCtx.texture, mipLevel: dstMipLevel, origin: {x: dstX, y: dstY, z: dstZ}},
                src.pixels,
                {bytesPerRow: src.width * bytesPerTexel},
                {width: src.width, height: src.height, depthOrArrayLayers: 1},
            )
        } else {
            // WebGL2 fallback
            switch (this.format) {
                case "r8": {
                    const dstPosition = new THREE.Vector3(dstX, dstY, dstZ)
                    const tempTex = new THREE.DataTexture(src.pixels, src.width, src.height, THREE.RedFormat, THREE.UnsignedByteType)
                    tempTex.colorSpace = THREE.LinearSRGBColorSpace
                    tempTex.flipY = false
                    tempTex.needsUpdate = true
                    this.renderer.copyTextureToTexture(tempTex, this.atlasTexture, null, dstPosition, 0, dstMipLevel)
                    tempTex.dispose()
                    break
                }
                case "rgba8": {
                    const staging = this.getOrCreateWebgl2RgbaStaging(dstMipLevel)
                    const tex = staging.getTexture(src.pixels, src.width, src.height)
                    const dstPosition = new THREE.Vector3(dstX, dstY, dstZ)
                    this.renderer.copyTextureToTexture(tex, this.atlasTexture, null, dstPosition, 0, dstMipLevel)
                    break
                }
                default:
                    assertNever(this.format)
            }
        }
    }

    private getOrCreateWebgl2RgbaStaging(dstMipLevel: number): IWebgl2RgbaStaging {
        const existing = dstMipLevel === 0 ? this._webgl2StagingMip0 : this._webgl2StagingMip1
        if (existing) return existing
        if (!this.createWebgl2RgbaStaging) {
            throw new Error("ThreeTextureTileAtlas: createWebgl2RgbaStaging factory is required for rgba8 format in WebGL2 fallback mode")
        }
        const mipScale = 1 << dstMipLevel
        const w = this._tileSizeWithMargin.width / mipScale
        const h = this._tileSizeWithMargin.height / mipScale
        const staging = this.createWebgl2RgbaStaging(w, h)
        if (dstMipLevel === 0) {
            this._webgl2StagingMip0 = staging
        } else {
            this._webgl2StagingMip1 = staging
        }
        return staging
    }

    private getGpuUploadContext(): {device: GPUDevice; texture: GPUTexture} | null {
        if (this._gpuDevice && this._gpuTexture) {
            return {device: this._gpuDevice, texture: this._gpuTexture}
        }
        const backend = this.renderer.backend as {device?: GPUDevice; get?: (t: unknown) => {texture?: GPUTexture}}
        const device = backend.device
        if (!device) {
            return null
        }
        let texture = backend.get?.(this._atlasTexture)?.texture
        if (!texture) {
            // Texture not yet registered with the backend; force GPU allocation so
            // writeTexture can be used immediately without waiting for a render frame.
            this.renderer.initTexture(this._atlasTexture)
            texture = backend.get?.(this._atlasTexture)?.texture
        }
        if (texture) {
            this._gpuDevice = device
            this._gpuTexture = texture
            return {device, texture}
        }
        return null
    }

    private readonly _tileSizeWithMargin: Size2Like
    private _numLayers = 0
    private _numTilesX = 0
    private _numTilesY = 0
    private _atlasTexture: THREE.DataArrayTexture
    private _gpuDevice: GPUDevice | null = null
    private _gpuTexture: GPUTexture | null = null
    private readonly _atlasTilePriorityQueue = new PriorityQueue<InternalAtlasTileInfo>()
    private readonly _atlasTileInfoByTextureTileId = new Map<TextureTileId, InternalAtlasTileInfo>()
    private _textureFiltering?: boolean

    private _webgl2StagingMip0: IWebgl2RgbaStaging | null = null
    private _webgl2StagingMip1: IWebgl2RgbaStaging | null = null
}

export type AtlasTileInfo = {
    layer: number
    tileIndex: Vector2Like
}

type InternalAtlasTileInfo = AtlasTileInfo & {
    occupiedBy: TextureTileId | null
    lastUsedFrame: number
}

const TILE_PRIORITY_LOCKED = -1
const TILE_PRIORITY_OCCUPIED = 0
const TILE_PRIORITY_FREE = 1000
